import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Box,
  Typography,
  IconButton,
  Button,
  Stack,
  Link as MLink,
  useMediaQuery,
  useTheme,
  Drawer,
  List,
  ListItemButton,
  ListItemText,
  Divider,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import NotificationsIcon from '@mui/icons-material/Notifications';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import LanguageSwitcher from '../LanguageSwitcher';

const Logo: React.FC = () => (
  <Box sx={{ display: 'flex', alignItems: 'center' }}>
    {/* If /logo.png is present in frontend/public it will be used; otherwise show text */}
    <Box component={Link} to="/" sx={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}>
      <Box
        component="img"
        src="/logo.png"
        alt="AutoReelr Logo"
        onError={(e: any) => { e.currentTarget.style.display = 'none'; e.currentTarget.nextSibling.style.display = 'block'; }}
        sx={{ height: 32, width: 32, mr: 1, display: 'block' }}
      />
      <Typography
        variant="h6"
        sx={{
          display: 'none',
          fontWeight: 800,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
        }}
      >
        AutoReelr
      </Typography>
    </Box>
  </Box>
);

const Header: React.FC<{ onMenuClick?: () => void }>= ({ onMenuClick }) => {
  const { isAuthenticated, user, logout } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const isActive = (to: string) => {
    if (to === '/') return location.pathname === '/';
    return location.pathname === to || location.pathname.startsWith(to + '/');
  };

  const NavLink: React.FC<{ to: string; label: string }>= ({ to, label }) => (
    <MLink
      component={Link}
      to={to}
      color={isActive(to) ? 'primary.main' : 'text.secondary'}
      underline="none"
      sx={{ fontWeight: 600, '&:hover': { color: 'primary.light' } }}
    >
      {label}
    </MLink>
  );

  return (
    <AppBar
      position="fixed"
      color="transparent"
      elevation={0}
      sx={{
        borderBottom: '1px solid rgba(255,255,255,0.06)',
        backgroundImage: 'linear-gradient(135deg, rgba(26,26,46,0.9) 0%, rgba(26,26,46,0.95) 100%)',
        backdropFilter: 'blur(10px)',
      }}
    >
      <Toolbar sx={{ minHeight: 72 }}>
        {isMobile ? (
          isAuthenticated ? (
            <IconButton edge="start" color="inherit" onClick={onMenuClick} sx={{ mr: 1 }} aria-label="Open menu">
              <MenuIcon />
            </IconButton>
          ) : (
            <IconButton edge="start" color="inherit" onClick={() => setMobileOpen(true)} sx={{ mr: 1 }} aria-label="Open menu">
              <MenuIcon />
            </IconButton>
          )
        ) : null}

        <Logo />

        <Box sx={{ flexGrow: 1 }} />

        {/* Left-side nav for desktop when unauthenticated */}
        {!isMobile && !isAuthenticated && (
          <Stack direction="row" spacing={3} sx={{ mr: 3 }}>
            <NavLink to="/about" label="About" />
            <NavLink to="/pricing" label="Pricing" />
          </Stack>
        )}

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <LanguageSwitcher />

          {isAuthenticated ? (
            <>
              <IconButton color="inherit" sx={{ color: 'text.secondary' }}>
                <NotificationsIcon />
              </IconButton>
              <Button
                variant="outlined"
                color="inherit"
                onClick={() => navigate('/dashboard')}
                sx={{ borderColor: 'rgba(255,255,255,0.2)' }}
              >
                Dashboard
              </Button>
              <Button
                variant="contained"
                onClick={() => logout()}
                sx={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                }}
              >
                Logout
              </Button>
            </>
          ) : (
            <>
              {!isMobile && (
                <Button
                  variant="text"
                  color="inherit"
                  onClick={() => navigate('/login')}
                  sx={{ color: 'text.secondary' }}
                >
                  Login
                </Button>
              )}
              <Button
                variant="contained"
                onClick={() => navigate('/signup')}
                sx={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                }}
              >
                Get Started
              </Button>
            </>
          )}
        </Box>
      </Toolbar>

      {/* Mobile Drawer for public navigation */}
      {/* Mobile Drawer for public navigation */}
      {!isAuthenticated && (
        <Drawer
          anchor="left"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          PaperProps={{
            sx: {
              width: 280,
              backgroundImage: 'linear-gradient(135deg, rgba(26,26,46,0.98) 0%, rgba(26,26,46,1) 100%)',
            },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', p: 2 }}>
            <Logo />
            <Box sx={{ flexGrow: 1 }} />
            <IconButton onClick={() => setMobileOpen(false)} aria-label="Close menu">
              <CloseIcon />
            </IconButton>
          </Box>
          <Divider />
          <List>
            {[{to: '/about', label: 'About'}, {to: '/pricing', label: 'Pricing'}].map(item => (
              <ListItemButton key={item.to} component={Link} to={item.to} onClick={() => setMobileOpen(false)}>
                <ListItemText primary={item.label} />
              </ListItemButton>
            ))}
          </List>
          <Box sx={{ p: 2, mt: 'auto' }}>
            <Button
              fullWidth
              variant="outlined"
              color="inherit"
              onClick={() => { setMobileOpen(false); navigate('/login'); }}
              sx={{ mb: 1, borderColor: 'rgba(255,255,255,0.2)' }}
            >
              Login
            </Button>
            <Button
              fullWidth
              variant="contained"
              onClick={() => { setMobileOpen(false); navigate('/signup'); }}
              sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}
            >
              Get Started
            </Button>
          </Box>
        </Drawer>
      )}
    </AppBar>
  );
};

export default Header;
